import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import axios from "axios";

interface ConnectionContextType {
  isConnected: boolean;
  lastPing: Date | null;
  serverTime: Date | null;
  checkConnection: () => Promise<void>;
}

const ConnectionContext = createContext<ConnectionContextType | undefined>(
  undefined
);

export const useConnection = () => {
  const context = useContext(ConnectionContext);
  if (context === undefined) {
    throw new Error("useConnection must be used within a ConnectionProvider");
  }
  return context;
};

export const ConnectionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isConnected, setIsConnected] = useState<boolean>(true);
  const [lastPing, setLastPing] = useState<Date | null>(null);
  const [serverTime, setServerTime] = useState<Date | null>(null);
  // Track the current check interval
  const [checkInterval, setCheckInterval] = useState<number>(10000);
  // Use ref to track the interval ID for cleanup
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const checkConnection = async () => {
    try {
      const response = await axios.get("/api/connection/health");
      setIsConnected(true);
      setLastPing(new Date());
      setServerTime(new Date(response.data.server_timestamp));
    } catch (error) {
      console.error("Connection check failed:", error);
      setIsConnected(false);
      setLastPing(new Date());
    }
  };

  // Effect to update interval based on connection status
  useEffect(() => {
    // If connected, check every 10 seconds, otherwise check every 2 seconds
    const newInterval = isConnected ? 10000 : 2000;
    setCheckInterval(newInterval);
  }, [isConnected]);

  useEffect(() => {
    // Initial connection check
    checkConnection();

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Set up interval with current checkInterval value
    intervalRef.current = setInterval(checkConnection, checkInterval);

    // Cleanup on unmount or when interval changes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkInterval]);

  return (
    <ConnectionContext.Provider
      value={{
        isConnected,
        lastPing,
        serverTime,
        checkConnection,
      }}
    >
      {children}
    </ConnectionContext.Provider>
  );
};
